/**
 * TeamsReorderModal — drag-and-drop reordering of "team blocks" in a plan.
 *
 * A team block = a project pill + everything (notes, photos, material lists)
 * up to the next project pill. Moving a block moves all of its child content
 * automatically, which is what the user asked for:
 *   "un tab cu proiecte sa il pot lua si muta la o o echipa mai jos,
 *    acesta automat ia pozele si listele din dreptul lui"
 *
 * Uses native HTML5 drag-and-drop (no external library).
 * Emits the reassembled body HTML via `onSave`.
 */
import React, { useMemo, useRef, useState } from "react";
import { X, GripVertical, HardHat, ArrowUp, ArrowDown } from "lucide-react";
import DOMPurify from "dompurify";
import { splitBodyIntoTeamBlocks, assembleBodyFromTeamBlocks } from "./planEditorHelpers";

const SAFE_TAGS = ["p", "br", "strong", "em", "u", "s", "mark", "h1", "h2", "ul", "ol", "li", "a", "span"];
const SAFE_ATTR = ["href", "target", "rel", "style", "class", "data-mlist", "data-project"];

function BlockPreview({ html }) {
  const clean = useMemo(
    () => DOMPurify.sanitize(html, { ALLOWED_TAGS: SAFE_TAGS, ALLOWED_ATTR: SAFE_ATTR }),
    [html],
  );
  return (
    <div
      className="text-sm text-slate-700 [&_a[href^='#list:']]:no-underline [&_a[href^='#list:']]:bg-amber-100 [&_a[href^='#list:']]:text-amber-900 [&_a[href^='#list:']]:px-1.5 [&_a[href^='#list:']]:py-0.5 [&_a[href^='#list:']]:rounded [&_a[href^='#list:']]:text-xs [&_a[href^='#list:']]:font-semibold [&_a[href^='#project:']]:no-underline [&_a[href^='#project:']]:bg-sky-100 [&_a[href^='#project:']]:text-sky-900 [&_a[href^='#project:']]:px-2 [&_a[href^='#project:']]:py-0.5 [&_a[href^='#project:']]:rounded-md [&_a[href^='#project:']]:font-black [&_a[href^='#project:']]:text-xs [&_p]:my-1"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

export default function TeamsReorderModal({ open, initialBody, onClose, onSave }) {
  const [blocks, setBlocks] = useState(() => splitBodyIntoTeamBlocks(initialBody || ""));
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  React.useEffect(() => {
    if (open) {
      setBlocks(splitBodyIntoTeamBlocks(initialBody || ""));
    }
  }, [open, initialBody]);

  if (!open) return null;

  const moveBlock = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) return;
    setBlocks((cur) => {
      const nx = cur.slice();
      const [item] = nx.splice(from, 1);
      nx.splice(to, 0, item);
      return nx;
    });
  };

  const onDragStart = (e, idx) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData for drag to start.
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== idx) setOverIndex(idx);
  };

  const onDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null || from === undefined) return;
    moveBlock(from, idx);
  };

  const onDragEnd = () => {
    dragIndex.current = null;
    setOverIndex(null);
  };

  const save = () => {
    const body = assembleBodyFromTeamBlocks(blocks);
    onSave(body);
  };

  const projectBlocks = blocks.filter((b) => b.projectId);
  const introBlock = blocks.find((b) => !b.projectId);

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reorder-backdrop"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="reorder-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Reordonează echipe</div>
            <div className="text-lg font-black text-slate-900">Trage o echipă pentru a o muta</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Fiecare card cuprinde proiectul + toate pozele și listele din dreptul lui.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="reorder-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {introBlock && (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3" data-testid="reorder-intro">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Intro (înainte de prima echipă)</div>
              <BlockPreview html={introBlock.html} />
            </div>
          )}

          {projectBlocks.length === 0 && (
            <div className="text-center py-10 text-sm text-slate-500 italic">
              Nu există nicio echipă/proiect în plan.
              <div className="text-[11px] text-slate-400 mt-1">Adaugă un proiect din toolbar apoi revino aici.</div>
            </div>
          )}

          {projectBlocks.map((b, i) => {
            // Index in the full blocks array (may differ from i if intro exists)
            const globalIdx = blocks.indexOf(b);
            const isOver = overIndex === globalIdx;
            return (
              <div
                key={b.projectId + i}
                draggable
                onDragStart={(e) => onDragStart(e, globalIdx)}
                onDragOver={(e) => onDragOver(e, globalIdx)}
                onDrop={(e) => onDrop(e, globalIdx)}
                onDragEnd={onDragEnd}
                className={`group rounded-xl border-2 bg-white transition ${
                  isOver ? "border-sky-500 bg-sky-50 scale-[1.01] shadow-md" : "border-slate-200 hover:border-sky-300"
                }`}
                data-testid={`reorder-block-${globalIdx}`}
              >
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
                  <HardHat className="w-4 h-4 text-sky-600" />
                  <div className="flex-1 text-xs font-black text-slate-900 truncate">
                    Echipa {i + 1}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveBlock(globalIdx, Math.max(0, globalIdx - 1))}
                      disabled={i === 0}
                      className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30"
                      title="Mută sus"
                      data-testid={`reorder-up-${globalIdx}`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBlock(globalIdx, Math.min(blocks.length - 1, globalIdx + 1))}
                      disabled={i === projectBlocks.length - 1}
                      className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30"
                      title="Mută jos"
                      data-testid={`reorder-down-${globalIdx}`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <BlockPreview html={b.html} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-200"
            data-testid="reorder-cancel"
          >
            Anulează
          </button>
          <button
            onClick={save}
            disabled={projectBlocks.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-black disabled:opacity-50"
            data-testid="reorder-save"
          >
            Aplică ordinea
          </button>
        </div>
      </div>
    </div>
  );
}
